import { Currency, IUserAppState, Invoice, ValidationErrors } from "packable-common";
import { SnackbarComponentVariant } from "packable-components";
import { IOrder } from "../orders/Order.types";

export enum ShippingCartViewType {
  CART_VIEW = "Cart View",
  INVOICE_VIEW = "Invoice View",
  PAYMENT_VIEW = "Payment View",
  ORDER_VIEW = "Order View"
}

export enum ShippingCartStatus {
  INACTIVE = "Inactive",
  ACTIVE = "Active",
}

export enum ShippingCartType {
  HOME = "Home",
  STORAGE = "Storage",
}

export type ShippingCartItem = {
  itemId: string;
  itemLabel: string;
  itemLength: number;
  itemWidth: number;
  itemHeight: number;
  itemWeight: number;
  storageEndDate: Date | null;
  useTheSameDateAndTime: boolean;
};

export class ShippingCartItemEntity implements ShippingCartItem {
  itemId: string = "";
  itemLabel: string = "";
  itemLength: number = 0;
  itemWidth: number = 0;
  itemHeight: number = 0;
  itemWeight: number = 0;
  storageEndDate: Date | null = null;
  useTheSameDateAndTime: boolean = true;
}

export type ShippingCart = {
  inventoryId: string | undefined;
  cartStatus: ShippingCartStatus;
  cartType: ShippingCartType;
  cartItems: Array<ShippingCartItem> | null;
};

export class ShippingCartEntity implements ShippingCart {
  inventoryId: string | undefined = "";
  cartStatus: ShippingCartStatus = ShippingCartStatus.INACTIVE;
  cartType: ShippingCartType = ShippingCartType.STORAGE;
  cartItems: ShippingCartItem[] | null = [];
}

export type ShippingCartViewProps = {
  displayMessage: (variant: SnackbarComponentVariant, message: string) => void;
  setDisplaySpinner: (flag: boolean) => void;
  userAppState: IUserAppState | null;
  handleCloseShippingCart: () => void;
  shippingCart: ShippingCart;
  onShippingCartUpdate: (shippingCart: ShippingCart) => void;
  validationErrorsList: Array<ValidationErrors>;
  onValidationErrorsListUpdate: (
    validationErrorsList: Array<ValidationErrors>
  ) => void;
  styles?: any;
};

export type ShippingCartItemViewProps = {
  shippingCartType: ShippingCartType;
  shippingCartItem: ShippingCartItem;
  multipleItems?: boolean;
  onShippingCartItemUpdate: (shippingCartItem: ShippingCartItem) => void;
  onRemoveShippingCartItem: (itemId: string) => void;
  validationErrors: ValidationErrors;
  onValidationErrorsUpdate: (validationErrors: ValidationErrors) => void;
  userAppState: IUserAppState | null;
  styles?: any;
};

export type ShippingCartDialogProps = {
  showShippingCart: boolean;
  handleCloseShippingCart: () => void;
  onOrderCompleted?: () => void;
  userAppState: IUserAppState | null;
  windowWidth?: number;
  windowHeight?: number;
  styles?: any;
};

export type ShippingCartItemPrice = {
  itemId: string;
  itemLabel: string;
  storageEndDate: Date;
  currency: Currency;
  subTotal: number;
  tax: number;
  total: number;
};

export class ShippingCartItemPriceEntity implements ShippingCartItemPrice {
  itemId: string = "";
  itemLabel: string = "";
  storageEndDate: Date = new Date();
  currency: Currency = Currency.USD;
  subTotal: number = 0;
  tax: number = 0;
  total: number = 0;
}

export type ShippingCartPrice = {
  cartType: ShippingCartType;
  currency: Currency;
  subTotal: number;
  tax: number;
  total: number;
  itemPrices: Array<ShippingCartItemPrice>;
};

export class ShippingCartPriceEntity implements ShippingCartPrice {
  cartType: ShippingCartType = ShippingCartType.STORAGE;
  currency: Currency = Currency.USD;
  subTotal: number = 0;
  tax: number = 0;
  total: number = 0;
  itemPrices: Array<ShippingCartItemPrice> = [];
}

export type InvoiceViewProps = {
  shippingCartPrice: ShippingCartPrice;
  styles?: any;
};

export type InvoiceItemViewProps = {
  shippingCartType: ShippingCartType;
  shippingCartItemPrice: ShippingCartItemPrice;
  styles?: any;
};

export type ShippingCartOrderRequest = {
  shippingCart: ShippingCart;
  provisionalInvoice: Invoice | null;
  token: string;
}

export class ShippingCartOrderRequestEntity implements ShippingCartOrderRequest {
  shippingCart: ShippingCart = new ShippingCartEntity();
  provisionalInvoice: Invoice | null = null;
  token: string = "";
}

export enum StorageOrderStatus {
  SUCCESS = "Success",
  VALIDATION_FAILED = "Validation Failed",
  PAYMENT_FAILED = "Payment Failed",
  FAILED = "Failed"
}

export type StorageOrder = {
  status: StorageOrderStatus;
  message: string;
  order: IOrder;
}